import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CallLog, subscribeLogsForCallID } from '@adornis/voice-assistant/call-logs.js';
import { firstValueFrom, timeout } from 'rxjs';
import { callConversation } from '../../db/call-analysis-resolvers.js';
import { testLabGetFeedback } from '../../db/test-lab-resolvers.js';
import { workTaskAnalysisIndex } from '../../db/work-task-analysis-resolvers.js';
import type { WorkTask } from '../../db/work-task-types.js';
import { formatCallLogsAsText } from './CallLogList.js';
import { formatCallTranscriptAsText } from './CallTranscript.js';

type AnalysisTask = Pick<WorkTask, '_id' | 'title' | 'feedbackLinks'>;
type Feedback = { callID: string; testerName: string; notes: string; updatedAt?: string | null };
type IndexPage = { tasks: AnalysisTask[]; feedback: Feedback[]; nextOffset: number | null };
const parse = <T>(value: unknown): T => JSON.parse(String(value)) as T;
const deadline = async <T>(operation: Promise<T>): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      operation,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('TIMEOUT')), 30000);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
};

export async function buildWorkTaskAnalysis(options: {
  task?: AnalysisTask;
  origin: string;
  progress: (text: string) => void;
  cancelled: () => boolean;
}): Promise<{ text: string; warnings: number }> {
  const check = () => {
    if (options.cancelled()) throw new Error('TASK_EXPORT_CANCELLED');
  };
  const tasks: AnalysisTask[] = options.task ? [options.task] : [];
  const feedback: Feedback[] = [];
  const warnings: string[] = [];
  const startedAt = new Date().toISOString();
  if (!options.task) {
    let offset: number | null = 0;
    while (offset !== null) {
      check();
      options.progress(`Tasks und Feedback laden… (${offset})`);
      const page: IndexPage = parse(await deadline(workTaskAnalysisIndex(offset)()));
      if (
        !Array.isArray(page.tasks) ||
        !Array.isArray(page.feedback) ||
        (page.nextOffset !== null && (!Number.isSafeInteger(page.nextOffset) || page.nextOffset <= offset))
      )
        throw new Error('TASK_EXPORT_INDEX_FAILED');
      tasks.push(...page.tasks);
      feedback.push(...page.feedback);
      offset = page.nextOffset;
    }
  }
  check();
  const uniqueTasks = [...new Map(tasks.map(task => [task._id, task])).values()];
  const calls = [
    ...new Set(
      [
        ...uniqueTasks.flatMap(task => task.feedbackLinks.map(link => link.callID)),
        ...feedback.map(item => item.callID),
      ].filter(Boolean),
    ),
  ];
  if (feedback.some(item => !item.callID))
    warnings.push('Feedback ohne Anruf-ID: Gespräch und Logs können nicht zugeordnet werden.');
  const parts = [
    'ZWICKROELL — TASKS, FEEDBACK UND ANRUFE',
    `System: ${options.origin}`,
    `Export begonnen: ${startedAt}`,
    `Umfang: ${options.task ? 'Ein Task und seine verknüpften Anrufe' : 'Alle Tasks und alle Feedback-Einträge, unabhängig von den Bildschirmfiltern'}`,
    'Momentaufnahme während des Exports. Identische Anrufe stehen einmal im Anhang. Aufgaben enthalten nur Titel und Zuordnung, keine Planungstexte, Stunden oder Freigaben.',
    ...uniqueTasks.map(task =>
      [
        `\n=== TASK: ${task.title} ===`,
        `Task-ID: ${task._id}`,
        `Verknüpfte Anrufe: ${task.feedbackLinks.map(link => link.callID).join(', ') || 'keine'}`,
        ...task.feedbackLinks.map(
          link =>
            `\nFeedback bei Verknüpfung — ${link.testerName || 'ohne Namen'} — ${link.callID}\n${link.notes || link.notesPreview || '(Kein Text)'}${link.notesTruncated ? '\n[Auszug gekürzt; aktuelles Feedback siehe Anhang.]' : !link.notes ? '\n[Gespeicherter Auszug; aktuelles Feedback siehe Anhang.]' : ''}`,
        ),
      ].join('\n'),
    ),
  ];
  if (!options.task)
    parts.push(
      '\n=== ALLE FEEDBACK-EINTRÄGE ===',
      ...feedback.map(
        item =>
          `\nAnruf: ${item.callID || '(ID fehlt)'}\nVon: ${item.testerName || 'ohne Namen'}\n${item.updatedAt ? `Geändert: ${item.updatedAt}\n` : ''}${item.notes}`,
      ),
    );
  // One call at a time, three independent read-only requests; duplicate attachments are fetched once.
  for (const [index, callID] of calls.entries()) {
    check();
    options.progress(`Anruf ${index + 1} von ${calls.length}: Gespräch, Feedback und Logs…`);
    const results = await Promise.allSettled([
      deadline(testLabGetFeedback(callID)()).then(raw => {
        const value = parse<{ testerName?: string; testerNotes?: string; testerNotesUpdatedAt?: string }>(raw);
        return `Von: ${value.testerName || 'ohne Namen'}\n${value.testerNotesUpdatedAt ? `Geändert: ${value.testerNotesUpdatedAt}\n` : ''}${value.testerNotes || '(Kein aktuelles Feedback vorhanden)'}`;
      }),
      deadline(callConversation(callID)()).then(raw => {
        const value = parse<{ turns: Parameters<typeof formatCallTranscriptAsText>[0] }>(raw);
        if (!Array.isArray(value.turns)) throw new Error('INVALID_TRANSCRIPT');
        return formatCallTranscriptAsText(value.turns) || '(Keine Gesprächsdaten vorhanden)';
      }),
      firstValueFrom(
        subscribeLogsForCallID(callID)(selectionSet(() => CallLog, 1)).pipe(timeout({ first: 30000 })),
      ).then(logs => {
        if (!Array.isArray(logs)) throw new Error('INVALID_LOGS');
        return formatCallLogsAsText(logs) || '(Keine Logs vorhanden)';
      }),
    ]);
    check();
    parts.push(`\n=== ANRUF: ${callID} ===`, `${options.origin}/calls/${encodeURIComponent(callID)}`);
    results.forEach((result, i) => {
      const section = ['Aktuelles Feedback', 'Gespräch', 'Logs'][i]!;
      parts.push(`\n--- ${section} ---`);
      if (result.status === 'fulfilled') parts.push(result.value);
      else {
        const warning = `${callID}: ${section} konnte nicht geladen werden.`;
        warnings.push(warning);
        parts.push(`[FEHLT: ${warning}]`);
      }
    });
  }
  check();
  parts.splice(
    5,
    0,
    `Ergebnis: ${uniqueTasks.length} Tasks, ${calls.length} unterschiedliche Anrufe. ${warnings.length ? 'UNVOLLSTÄNDIG: ' + warnings.join(' | ') : 'Alle angeforderten Abschnitte wurden geladen.'}`,
  );
  return { text: parts.join('\n'), warnings: warnings.length };
}

export function downloadWorkTaskAnalysis(text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `zwickroell-analyse-${new Date().toISOString().slice(0, 10)}.txt`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
